import React, { memo, type MouseEvent } from 'react';
import { styled } from '@compiled/react';
import noop from 'lodash/noop';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button';
import ChevronDownLegacy from '@atlaskit/icon/glyph/chevron-down';
import ChevronRightLegacy from '@atlaskit/icon/glyph/chevron-right';
import ChevronDownIcon from '@atlaskit/icon/utility/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/utility/chevron-right';
import { token } from '@atlaskit/tokens';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { Tokens } from '@atlassian/jira-custom-theme-constants/src/constants.tsx';
import { useSoftwareProjectTheming } from '@atlassian/jira-project-theme-providers/src/controllers/software-theme/index.tsx';
import { getIsSoftwareThemingM2Enabled } from '@atlassian/jira-project-theme-providers/src/utils/getIsSoftwareThemingEnabled.tsx';
import messages from './messages.tsx';

export type Props = {
	isExpanded: boolean;
	onToggle: (event: MouseEvent<HTMLElement>, analyticsEvent: UIAnalyticsEvent) => void;
	itemName: string;
	itemType: 'epic' | 'version';
	isActive?: boolean;
};

const Expand = ({
	isExpanded = false,
	onToggle = noop,
	itemName,
	itemType,
	isActive = false,
}: Props) => {
	const { formatMessage } = useIntl();
	const { isSoftwareFullTheming } = getIsSoftwareThemingM2Enabled()
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useSoftwareProjectTheming()
		: { isSoftwareFullTheming: false };
	const primaryColor = token('color.icon');

	const collapseLabel =
		itemType === 'epic'
			? formatMessage(messages.collapseEpicLabel, { epicName: itemName })
			: formatMessage(messages.collapseVersionLabel, { versionName: itemName });
	const expandLabel =
		itemType === 'epic'
			? formatMessage(messages.expandEpicLabel, { epicName: itemName })
			: formatMessage(messages.expandVersionLabel, { versionName: itemName });

	const iconColor =
		isVisualRefreshEnabled() && isActive ? token('color.text.selected') : primaryColor;

	return (
		<ButtonWrapper
			visualRefresh={isVisualRefreshEnabled()}
			visualRefreshDrop6={fg('visual-refresh_drop_6')}
		>
			<Button
				testId="software-backlog-panel.common.ui.card.expand"
				aria-label={isExpanded ? collapseLabel : expandLabel}
				iconBefore={
					isExpanded ? (
						<ChevronDownIcon
							label=""
							// @ts-expect-error value does not match type - needed for theming
							color={isSoftwareFullTheming && isActive ? Tokens.COLOR_TEXT : iconColor}
							spacing={isVisualRefreshEnabled() ? 'spacious' : 'compact'}
							LEGACY_size="medium"
							LEGACY_fallbackIcon={ChevronDownLegacy}
						/>
					) : (
						<ChevronRightIcon
							label=""
							// @ts-expect-error value does not match type - needed for theming
							color={isSoftwareFullTheming && isActive ? Tokens.COLOR_TEXT : iconColor}
							spacing={isVisualRefreshEnabled() ? 'spacious' : 'compact'}
							LEGACY_size="medium"
							LEGACY_fallbackIcon={ChevronRightLegacy}
						/>
					)
				}
				appearance="subtle-link"
				spacing="none"
				onClick={onToggle}
			/>
		</ButtonWrapper>
	);
};

export { Expand };

export default memo<Props>(Expand);

const ICON_SIZE = gridSize * 3;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ButtonWrapper = styled.div<{
	visualRefresh: boolean;
	visualRefreshDrop6: boolean;
}>(
	{
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		height: `${ICON_SIZE}px`,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		width: `${ICON_SIZE}px`,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	({ visualRefresh }) =>
		visualRefresh && {
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
		},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	({ visualRefresh, visualRefreshDrop6 }) =>
		visualRefresh &&
		!visualRefreshDrop6 && {
			marginTop: token('space.025'),
		},
);
